import React from "react";
import PropTypes from "prop-types";
import { fmtEur, fmtEurSigned, fmtSmape } from "./format";
import "../../../../components/ui-components/DataTable.css";

// AllocationSourceRankTable mirrors SeriesRankTable's styling but ranks
// allocation cost sources (source_key) by projected spend. Columns:
// Source · Spend · Growth · Model · sMAPE. Rows come pre-sorted (spend desc)
// from the backend; we keep them in given order.
export default function AllocationSourceRankTable({
  rows,
  loading,
  selected,
  onSelect,
  // Also accept shell naming (data / isLoading).
  data,
  isLoading,
}) {
  const effectiveRows = rows ?? data ?? [];
  const effectiveLoading = loading ?? isLoading ?? false;

  if (effectiveLoading) {
    return (
      <div className="px-4 py-8 text-center text-sm text-text-3">
        Loading sources…
      </div>
    );
  }

  if (!effectiveRows || effectiveRows.length === 0) {
    return (
      <div className="px-4 py-12 text-center">
        <p className="text-sm text-text-3">No allocation sources available.</p>
        <p className="mt-1 text-xs text-text-3">
          Select a horizon to load per-source forecasts.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-card border border-card-bd bg-card">
      <table className="data-table">
        <thead>
          <tr>
            <th>
              Source
            </th>
            <th>
              Spend
            </th>
            <th>
              Growth
            </th>
            <th>
              Model · sMAPE
            </th>
          </tr>
        </thead>
        <tbody>
          {effectiveRows.map((row) => {
            const isSelected = selected === row.source_key;
            const growthPositive = (row.growth_eur || 0) > 0;

            return (
              <tr
                key={row.source_key}
                onClick={() => onSelect && onSelect(row.source_key)}
                className={`cursor-pointer transition-colors hover:bg-surface-2 ${
                  isSelected ? "bg-primary-soft hover:bg-primary-soft" : ""
                }`}
              >
                {/* Source key */}
                <td>
                  <span
                    className={`font-medium ${
                      isSelected ? "text-primary" : "text-text"
                    }`}
                  >
                    {row.source_key}
                  </span>
                </td>

                {/* Projected spend */}
                <td className="tabular-nums text-text-2">
                  {fmtEur(row.projected_spend)}
                </td>

                {/* Growth — red/up when positive, green when negative */}
                <td className="tabular-nums">
                  <div className="flex items-center gap-1.5">
                    {growthPositive && row.growth_eur != null && (
                      <span className="text-xs text-danger" aria-hidden="true">
                        ↑
                      </span>
                    )}
                    <span
                      className={
                        row.growth_eur != null && growthPositive
                          ? "font-medium text-danger"
                          : row.growth_eur != null && row.growth_eur < 0
                          ? "font-medium text-success"
                          : "text-text-3"
                      }
                    >
                      {fmtEurSigned(row.growth_eur)}
                    </span>
                  </div>
                </td>

                {/* Model · sMAPE */}
                <td className="text-text-3">
                  {row.algorithm ? (
                    <span>
                      <span className="font-medium text-text-2">
                        {row.algorithm}
                      </span>
                      <span className="mx-1 text-text-3">·</span>
                      <span
                        className={
                          row.smape == null ? "text-text-3 italic" : "tabular-nums"
                        }
                      >
                        {fmtSmape(row.smape)}
                      </span>
                    </span>
                  ) : (
                    <span className="text-text-3 italic">not yet rated</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

AllocationSourceRankTable.propTypes = {
  rows: PropTypes.array,
  data: PropTypes.array,
  loading: PropTypes.bool,
  isLoading: PropTypes.bool,
  selected: PropTypes.string,
  onSelect: PropTypes.func,
};
